import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

interface PublicProfileData {
  id: string;
  username: string;
  displayName: string;
  profilePicture: string | null;
}

const PublicProfile: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [profile, setProfile] = useState<PublicProfileData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { user } = useAuth();

  useEffect(() => {
    async function fetchProfile() {
      try {
        setLoading(true);
        setError(null);
        
        const apiBase = import.meta.env.VITE_API_BASE || '/api';
        const response = await fetch(`${apiBase}/users/${id}`, {
          cache: 'no-store'
        });
        
        if (!response.ok) {
          if (response.status === 404) {
            setError('User not found');
          } else {
            setError('Failed to load profile');
          }
          setProfile(null);
          return;
        }
        
        const data = await response.json();
        setProfile(data.user);
      } catch (error) {
        console.error('Error loading profile:', error);
        setError('Something went wrong while loading the profile');
        setProfile(null);
      } finally {
        setLoading(false);
      }
    }
    
    if (id) {
      fetchProfile();
    }
  }, [id]);

  // If viewing your own profile, redirect to the full profile page
  if (user && user.id === id) {
    window.location.href = '/profile';
    return null;
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="max-w-2xl mx-auto">
        {loading ? (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-naia-pink"></div>
          </div>
        ) : error ? (
          <div className="text-center py-16">
            <p className="text-xl text-red-400 mb-4">{error}</p>
            <button
              onClick={() => window.location.reload()}
              className="px-4 py-2 bg-naia-pink text-pearl-white rounded hover:bg-opacity-80 transition"
            >
              Try Again
            </button>
          </div>
        ) : profile ? (
          <div className="bg-gray-800 rounded-xl p-8 shadow-lg">
            <div className="flex flex-col md:flex-row items-center md:items-start gap-8">
              {/* Profile Picture */}
              <div className="w-32 h-32 rounded-full overflow-hidden bg-gray-700 flex-shrink-0 flex items-center justify-center">
                {profile.profilePicture ? (
                  <img 
                    src={profile.profilePicture} 
                    alt={profile.displayName} 
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <span className="text-4xl text-white font-medium">
                    {profile.displayName.charAt(0).toUpperCase()}
                  </span>
                )}
              </div>
              
              {/* Profile Info */}
              <div className="flex-1 text-center md:text-left">
                <h1 className="text-3xl font-bold text-white mb-2">{profile.displayName}</h1>
                <p className="text-gray-400 mb-6">@{profile.username}</p>
                
                <div className="bg-gray-700 rounded-lg p-6 mt-4">
                  <p className="text-white text-lg">
                    Full profile page coming soon!
                  </p>
                </div>
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default PublicProfile;
